import { FileTextIcon } from 'lucide-react'

import { membersPlanDocuments } from 'rails_routes'
import { Banner, SolidCircleCheckIcon, SolidCircleXIcon, TextLink } from 'slab'
import { getFormattedDate } from 'utils'

import { useOnboarding } from '../OnboardingContext'
import { type OnboardingAllowance, type Restrictions } from '../types'
import { allowanceIsEmpty } from '../utils'

const getBenefitsList = (
  restrictions: Restrictions | undefined,
  allowance?: OnboardingAllowance | null
) => {
  if (!allowance || allowanceIsEmpty(allowance)) {
    return { included: [], notIncluded: [], errorMessages: [] }
  }

  const included: string[] = ['Medical']
  const notIncluded: string[] = []
  const errorMessages: string[] = []

  if (!restrictions?.dental_not_available) {
    included.push('Dental')
  } else {
    notIncluded.push('Dental')
  }

  if (!restrictions?.vision_not_available) {
    included.push('Vision')
  } else {
    notIncluded.push('Vision')
  }

  if (allowance.qme_treatment === 'all_allowed') {
    included.push('Other healthcare expenses')
  } else if (allowance.qme_treatment === 'disabled') {
    notIncluded.push('Other healthcare expenses')
  } else if (allowance.qme_treatment === 'copay_only') {
    included.push('Other healthcare expenses (Copay only)')
  } else {
    // ideally this should not be a blocker for the entire allowance flow, so rendering the error message instead.
    errorMessages.push(
      'You have an unexpected QME treatment value - please reach out to the support for more help on this.'
    )
  }

  return { included, notIncluded, errorMessages }
}

const IncludedBanner = ({
  benefit,
  included,
}: {
  benefit: string;
  included: boolean;
}) => {
  return (
    <li className='flex items-center gap-1'>
      {included
        ? (
          <SolidCircleCheckIcon size={16} className='text-green-400' />
          )
        : (
          <SolidCircleXIcon size={16} className='text-red-400' />
          )}
      <span className='body-bold text-gray-800'>
        {benefit}
      </span>
    </li>
  )
}

const IncludedSection = ({
  benefits,
  included,
}: {
  benefits: string[];
  included: boolean;
}) => {
  if (benefits.length === 0) return null

  const hasQmeTreatment = benefits.some((benefit) =>
    benefit.toLowerCase().includes('other healthcare expenses')
  )

  return (
    <>
      <section
        data-testid={included ? 'included-section' : 'not-included-section'}
        className='flex flex-col gap-2'
      >
        <h2 className='body text-gray-500'>
          {included ? 'including:' : 'but not including:'}
        </h2>
        <ul className='flex flex-wrap gap-x-4 gap-y-1 items-center'>
          {benefits.map((benefit) => (
            <IncludedBanner
              key={benefit}
              benefit={benefit}
              included={included}
            />
          ))}
        </ul>
      </section>
      {!included && hasQmeTreatment && (
        <Banner
          title={
            <p>
              <b>Your plan doesn't include other healthcare expenses</b>, so any
              money not spent on plan premiums will go unused.
            </p>
          }
          type='notice'
        />
      )}
    </>
  )
}

export function AllowanceBanner () {
  const { employerName, onboardingData } = useOnboarding()

  const planYear = onboardingData?.enrollment_event.plan_year
  const soonestCoverageStartDate =
    onboardingData?.member?.soonest_coverage_start_date

  const { included, notIncluded, errorMessages } = getBenefitsList(
    onboardingData?.restrictions,
    onboardingData?.allowance
  )

  return (
    <div className='flex flex-col gap-y-6 rounded-lg p-6 border-emphasis shadow-emphasis'>
      <div className='flex flex-col md:flex-row gap-4 xs:justify-between xs:align-top'>
        <div className='flex flex-col gap-4'>
          <div>
            <p className='text-gray-500 body'>{employerName} is offering</p>
            <p className='text-gray-800 heading-xl'>
              {onboardingData?.allowance?.allowance_amount?.formatted_amount}
              <span className='text-gray-500 body-sm'>/mo</span>
            </p>
          </div>
          {soonestCoverageStartDate && (
            <div className='flex flex-col gap-y-1'>
              <p className='body text-gray-500'>
                for your healthcare costs, starting on:
              </p>
              <p className='body-bold text-gray-800'>
                {getFormattedDate(soonestCoverageStartDate, {
                  month: 'long',
                  timeZone: 'UTC',
                })}
              </p>
            </div>
          )}
        </div>
        <div className='body-sm'>
          <TextLink
            color='gray'
            leftIcon={FileTextIcon}
            href={membersPlanDocuments.sbc({ plan_year: planYear })}
            target='_blank'
          >
            Summary of your benefits offer
          </TextLink>
        </div>
      </div>
      <IncludedSection benefits={included} included />
      <IncludedSection benefits={notIncluded} included={false} />
      {errorMessages && errorMessages.length > 0 && (
        <Banner
          title="There is a potential issue with your plan's benefits"
          type='caution'
        >
          <ul className='flex flex-col gap-2'>
            {errorMessages.map((message) => (
              <li key={message} className='body text-gray-800'>
                {message}
              </li>
            ))}
          </ul>
        </Banner>
      )}
    </div>
  )
}
